import produce from 'immer';

import { DisplayCondition } from '@components/form';
import { Field } from '@components/form/dynamic-fields';

export const createFieldNameToFieldIdMap = <T extends Field>(form: T[]) => {
  let result: Record<string, string> = {};

  form.forEach((field) => {
    if ('name' in field && field.name) {
      result[field.name] = field.id;
    }

    if ('fields' in field) {
      result = {
        ...result,
        ...createFieldNameToFieldIdMap(field.fields),
      };
    }
  });

  return result;
};

const assignConditionPropertyIdFromName = (
  condition: DisplayCondition,
  nameToIdMap: Record<string, string>
) => {
  return produce(condition, (draft) => {
    if ('on' in draft) {
      // 'on' only exists in the old type, so do nothing
      return;
    } else if (draft.AND) {
      draft.AND = draft.AND.map((condition) =>
        assignConditionPropertyIdFromName(condition, nameToIdMap)
      );
    } else if (draft.OR) {
      draft.OR = draft.OR.map((condition) =>
        assignConditionPropertyIdFromName(condition, nameToIdMap)
      );
    } else if (draft.NOT) {
      assignConditionPropertyIdFromName(draft.NOT, nameToIdMap);
    } else if (draft.IS) {
      assignConditionPropertyIdFromName(draft.IS, nameToIdMap);
    } else {
      if (draft.property in nameToIdMap) {
        draft.property = nameToIdMap[draft.property];
      }
    }
  });
};

// Since names can accidentally be duplicated,
// we cannot rely on `name` being unique and thus cannot use it as ID.
// However, the name is being used in ConditionalField conditions, and
// we need to remap that name over to the ID that was generated during
// instantiation so the condition can correctly watch the form state
// values for the unique ID corresponding to that name.
export const remapConditionProperties = <T extends Field>(
  form: T[],
  nameToIdMap: Record<string, string>
) => {
  return produce(form, (draft) => {
    draft.forEach((field) => {
      if (field.type === 'conditional') {
        field.condition = assignConditionPropertyIdFromName(field.condition, nameToIdMap);
      }

      if ('fields' in field) {
        field.fields = remapConditionProperties(field.fields, nameToIdMap);
      }
    });
  });
};
